/**
 * LocalStorage state management for signup wizard
 */

import { safeStorageGet, safeStorageSet, safeStorageRemove } from '../../safe-storage.js';

const STORAGE_KEY = 'signupWizardState';

// Position keys that must not stay in client storage. loadState() removes them.
const POSITION_KEYS = ['userLat', 'userLon', 'geoObservation'];

// saveState is wired to a $watch on every wizard field, so it runs once per
// keystroke, and a storage area that will not accept the write keeps refusing
// for the whole session. Reporting that per write would emit hundreds of
// identical lines and bury the breadcrumb trail a reported signup failure
// depends on, so it is reported once and then treated as the standing
// condition it is.
let reportedStorageFailure = false;

function reportStorageFailureOnce(message) {
    if (reportedStorageFailure) return;

    reportedStorageFailure = true;
    console.error(message);
}

export function loadState() {
    try {
        const savedState = safeStorageGet(STORAGE_KEY);
        if (savedState) {
            const parsed = JSON.parse(savedState);

            // Validation flags reset on every page load.
            delete parsed.touched;
            delete parsed.dirty;

            if (POSITION_KEYS.some((key) => key in parsed)) {
                // restoreGeoState() reads the recorded outcome from geoPermissionStatus only.
                if (!parsed.geoPermissionStatus && parsed.geoObservation?.permission_status) {
                    parsed.geoPermissionStatus = parsed.geoObservation.permission_status;
                }
                POSITION_KEYS.forEach((key) => delete parsed[key]);
                safeStorageSet(STORAGE_KEY, JSON.stringify(parsed));
            }

            // Format values for consistency
            if (parsed.day) parsed.day = String(parsed.day).padStart(2, '0');
            if (parsed.month) parsed.month = String(parsed.month).padStart(2, '0');
            if (parsed.year) parsed.year = String(parsed.year);

            return parsed;
        }
    } catch (e) {
        console.error('[Signup] Failed to load state:', e);
    }
    return null;
}

export function saveState(state) {
    const stateToSave = {
        currentStep: state.currentStep,
        name: state.name || '',
        email: state.email || '',
        day: state.day ? String(state.day).padStart(2, '0') : '',
        month: state.month ? String(state.month).padStart(2, '0') : '',
        year: state.year ? String(state.year) : '',
        sex: state.sex || '',
        username: state.username || '',
        // Password intentionally NOT persisted - security risk if stored in localStorage
        cityId: state.cityId || '',
        cityName: state.cityName || '',
        cityCountryIso: state.cityCountryIso || '',
        homeCityId: state.homeCityId || '',
        homeCityName: state.homeCityName || '',
        homeCityCountryIso: state.homeCityCountryIso || '',
        // Don't persist touched/dirty state - validation should reset on page reload
        locationRequested: state.locationRequested || false,
        locationReady: state.locationReady || false,
        // A device position never enters client storage (docs#10). Only the
        // permission outcome is kept, and restoreGeoState() reads it.
        geoPermissionStatus: state.geoObservation?.permission_status || '',
        geoExplainerShown: state.geoExplainerShown || false
    };

    if (!safeStorageSet(STORAGE_KEY, JSON.stringify(stateToSave))) {
        reportStorageFailureOnce('[Signup] Failed to save state');
    }
}

export function clearState() {
    if (!safeStorageRemove(STORAGE_KEY)) {
        reportStorageFailureOnce('[Signup] Failed to clear state');
    }
}
